import { createContext, useContext, useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SavedContext = createContext(null);
const hiddenKey = (uid) => `nikah_saved_hidden_${uid || "anon"}`;

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.id;
  const [ids, setIds] = useState(() => new Set());
  const [hidden, setHidden] = useState(() => new Set());

  useEffect(() => {
    try { const raw = localStorage.getItem(hiddenKey(uid)); setHidden(new Set(raw ? JSON.parse(raw) : [])); }
    catch { setHidden(new Set()); }
  }, [uid]);

  const load = useCallback(async () => {
    if (!uid) return;
    try { const { data } = await api.get("/saved"); setIds(new Set((data.profiles || []).map((p) => p.id))); } catch {}
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const persistHidden = (next) => { try { localStorage.setItem(hiddenKey(uid), JSON.stringify([...next])); } catch {} };

  // Display state: server-saved AND not locally hidden.
  const isSaved = useCallback((id) => ids.has(id) && !hidden.has(id), [ids, hidden]);

  const save = useCallback(async (id) => {
    await api.post("/actions", { targetId: id, action: "saved" });
    setIds((prev) => new Set(prev).add(id));
    setHidden((prev) => { const n = new Set(prev); n.delete(id); persistHidden(n); return n; });
  }, [uid]); // eslint-disable-line

  // Prefer a real server unsave (DELETE /saved/{id}); fall back to a local device-only hide
  // when the production backend doesn't yet expose an unsave endpoint.
  const removeLocal = useCallback(async (id) => {
    try {
      await api.delete(`/saved/${id}`);
      setIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      setHidden((prev) => { const n = new Set(prev); n.delete(id); persistHidden(n); return n; });
    } catch {
      setHidden((prev) => { const n = new Set(prev).add(id); persistHidden(n); return n; });
    }
  }, [uid]); // eslint-disable-line

  const toggle = useCallback(async (id) => {
    if (ids.has(id) && !hidden.has(id)) { await removeLocal(id); return false; }
    await save(id); return true;
  }, [ids, hidden, save, removeLocal]);

  return (
    <SavedContext.Provider value={{ isSaved, save, removeLocal, toggle, reload: load }}>
      {children}
    </SavedContext.Provider>
  );
}

export const useSaved = () => useContext(SavedContext) || {};
